// The anchoring look registry: one World theme look id in, one recipe out
// (R1125, `ui-map-interface.md` §6.1.5).
//
// One field of a World body names an anchoring look: `anchoring`, which is
// what a hull draws while its anchor goes down, holds and comes up. The
// player picks one of eleven; this module turns the id into the draw code and
// the figure row, and decides nothing else.
//
// **The arithmetic is not here.** `anchor-recipe-kit.ts` holds the context
// type, the eases, the numerals and every primitive. This module re-exports
// the kit, so an importer takes one name from one place.
//
// **A look resolves through its own recipe file.** The draw code of a card
// lives in `anchor-recipes-{core,cable,frame,ground}.ts`, keyed by the look
// id. The four files hold DISJOINT sets of ids, so each port lane edits its
// own file. A look that its file lists in `PENDING` draws the AUTO look — its
// RECIPE — so every id a player can pick draws something from the day its
// data lands. A pending look keeps its own figure row, which the AUTO recipe
// does not read: the AUTO recipe draws with the AUTO row.
//
// This is a leaf (`leaves.test.ts`).

import { ANCHOR_STYLES } from '../gen/balance';
import { ANCHOR_LOOKS } from '../gen/presentation';
import type { AnchorRow } from '../gen/presentation';
import type { AnchorRecipe } from './anchor-recipe-kit';
import { RECIPES as CORE, PENDING as CORE_PENDING } from './anchor-recipes-core';
import { RECIPES as CABLE, PENDING as CABLE_PENDING } from './anchor-recipes-cable';
import { RECIPES as FRAME, PENDING as FRAME_PENDING } from './anchor-recipes-frame';
import { RECIPES as GROUND, PENDING as GROUND_PENDING } from './anchor-recipes-ground';
import { lookRegistry, type Look, type RecipeFile } from './recipe-kit';

export * from './anchor-recipe-kit';

/** The World field that names an anchoring look. */
export const ANCHOR_FIELD = 'anchoring';

/** Every look id, in cycle order. Position 0 is the AUTO look (R822). */
export const ANCHOR_IDS: readonly string[] = ANCHOR_STYLES;

/** The AUTO look: the id every pending look draws. */
export const ANCHOR_AUTO = ANCHOR_IDS[0];

/** One resolved look: the id asked for, its figures and its draw code. */
export type AnchorLook = Look<AnchorRow, AnchorRecipe>;

/**
 * The four recipe files. They hold disjoint id sets, and
 * `anchor-styles.test.ts` pins that: two files that claimed one id would let
 * a merge decide which recipe draws.
 */
const FILES: readonly RecipeFile<AnchorRecipe>[] = [
  { recipes: CORE, pending: CORE_PENDING },
  { recipes: CABLE, pending: CABLE_PENDING },
  { recipes: FRAME, pending: FRAME_PENDING },
  { recipes: GROUND, pending: GROUND_PENDING },
];

const REGISTRY = lookRegistry(ANCHOR_IDS, FILES, ANCHOR_LOOKS);

/** The look of one id, or the AUTO look for an id this build does not hold. */
export const anchorLook: (id: string) => AnchorLook = REGISTRY.look;

/** Which ids each recipe file has built, and which it left pending. */
export const fileHalves = REGISTRY.fileHalves;
